/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';

import { SpaceSubModuleEnum } from '@coze-foundation/space-ui-adapter';
import { GlobalError } from '@coze-foundation/layout';
import { BaseEnum } from '@coze-arch/web-context';

import { Layout } from '../layout';
import {
  LoginPage,
  SpaceLayout,
  SpaceIdLayout,
  Develop,
  AgentIDELayout,
  AgentIDE,
  AgentPublishPage,
  Redirect,
  spaceSubMenu,
  WorkflowPage,
  SearchPage,
  ProjectIDE,
  ProjectIDEPublish,
  Library,
  Knowledge,
  Models,
  Skills,
  Harness,
  Logs,
  Billing,
  PromptEngineering,
  Evaluation,
  Members,
  OperationLogs,
  PluginLayout,
  PluginToolPage,
  PluginPage,
  KnowledgePreview,
  KnowledgeUpload,
  DatabaseDetail,
  ExplorePluginPage,
  ExploreTemplatePage,
  OAuthConsentConfirmPage,
  AgentSharePage,
  HarnessSharePage,
} from './async-components';

export const router: ReturnType<typeof createBrowserRouter> =
  createBrowserRouter([
    {
      path: '/share/:share_code',
      Component: AgentSharePage,
      errorElement: <GlobalError />,
      loader: () => ({
        hasSider: false,
        requireAuth: false,
      }),
    },
    {
      path: '/harness/share/:share_code',
      Component: HarnessSharePage,
      errorElement: <GlobalError />,
      loader: () => ({
        hasSider: false,
        requireAuth: false,
      }),
    },
    // Document routing
    {
      path: '/open/docs/*',
      Component: Redirect,
      loader: () => ({
        hasSider: false,
        requireAuth: false,
      }),
    },
    {
      path: '/docs/*',
      Component: Redirect,
      loader: () => ({
        hasSider: false,
        requireAuth: false,
      }),
    },
    {
      path: '/information/auth/success',
      Component: Redirect,
      loader: () => ({
        hasSider: false,
        requireAuth: false,
      }),
    },
    // main application route
    {
      path: '/',
      Component: Layout,
      errorElement: <GlobalError />,
      children: [
        {
          index: true,
          element: <Navigate to="/space" replace />,
        },
        // login page routing
        {
          path: 'sign',
          Component: LoginPage,
          errorElement: <GlobalError />,
          loader: () => ({
            hasSider: false,
            requireAuth: false,
          }),
        },

        // OAuth consent confirm page
        {
          path: 'oauth/confirm',
          Component: OAuthConsentConfirmPage,
          errorElement: <GlobalError />,
          loader: () => ({
            hasSider: false,
            requireAuth: false,
          }),
        },

        // Workspace Routing
        {
          path: 'space',
          Component: SpaceLayout,
          loader: () => ({
            hasSider: true,
            requireAuth: true,
            subMenu: spaceSubMenu,
            menuKey: BaseEnum.Space,
          }),
          children: [
            {
              path: ':space_id',
              Component: SpaceIdLayout,
              children: [
                {
                  index: true,
                  element: <Navigate to="develop" replace />,
                },

                // Project Development
                {
                  path: 'develop',
                  Component: Develop,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.DEVELOP,
                  }),
                },

                // Agent IDE
                {
                  path: 'bot/:bot_id',
                  Component: AgentIDELayout,
                  children: [
                    {
                      index: true,
                      Component: AgentIDE,
                    },
                    {
                      path: 'publish',
                      children: [
                        {
                          index: true,
                          Component: AgentPublishPage,
                          loader: () => ({
                            hasSider: false,
                            requireBotEditorInit: false,
                            pageName: 'publish',
                          }),
                        },
                      ],
                    },
                  ],
                  loader: () => ({
                    hasSider: false,
                    showMobileTips: true,
                    requireBotEditorInit: true,
                    pageName: 'bot',
                  }),
                },

                // Project IDE
                {
                  path: 'project-ide/:project_id/publish',
                  loader: () => ({
                    hasSider: false,
                  }),
                  Component: ProjectIDEPublish,
                },
                {
                  path: 'project-ide/:project_id/*',
                  Component: ProjectIDE,
                  loader: () => ({
                    hasSider: false,
                  }),
                },

                // resource library
                {
                  path: 'library',
                  Component: Library,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.LIBRARY,
                  }),
                },

                {
                  path: 'models',
                  Component: Models,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.MODELS,
                  }),
                },

                {
                  path: 'prompt-engineering/*',
                  Component: PromptEngineering,
                  loader: ({ request }) => ({
                    subMenuKey: new URL(request.url).pathname.includes(
                      '/prompt-engineering/playground',
                    )
                      ? SpaceSubModuleEnum.PROMPT_PLAYGROUND
                      : SpaceSubModuleEnum.PROMPT_ENGINEERING,
                  }),
                },

                {
                  path: 'evaluation/*',
                  Component: Evaluation,
                  loader: ({ request }) => {
                    const pathname = new URL(request.url).pathname;
                    return {
                      subMenuKey: pathname.includes('/evaluation/datasets')
                        ? SpaceSubModuleEnum.EVALUATION_DATASETS
                        : pathname.includes('/evaluation/evaluators')
                          ? SpaceSubModuleEnum.EVALUATION_EVALUATORS
                          : SpaceSubModuleEnum.EVALUATION_EXPERIMENTS,
                    };
                  },
                },

                {
                  path: 'skills',
                  Component: Skills,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.SKILLS,
                  }),
                },
                {
                  path: 'skills/create',
                  Component: Skills,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.SKILLS,
                  }),
                },
                {
                  path: 'skills/:skill_id',
                  Component: Skills,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.SKILLS,
                  }),
                },
                {
                  path: 'skills/:skill_id/edit',
                  Component: Skills,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.SKILLS,
                  }),
                },
                {
                  path: 'skills/:skill_id/copy',
                  Component: Skills,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.SKILLS,
                  }),
                },

                {
                  path: 'harness',
                  Component: Harness,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.HARNESS,
                  }),
                },

                // execution logs
                {
                  path: 'logs',
                  Component: Logs,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.LOGS,
                  }),
                },
                {
                  path: 'billing',
                  Component: Billing,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.BILLING,
                  }),
                },
                {
                  path: 'members',
                  Component: Members,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.MEMBERS,
                  }),
                },
                {
                  path: 'operation-logs',
                  Component: OperationLogs,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.OPERATION_LOGS,
                  }),
                },

                // Knowledge Base Resources
                {
                  path: 'knowledge',
                  children: [
                    {
                      index: true,
                      Component: Knowledge,
                    },
                    {
                      path: ':dataset_id',
                      element: <KnowledgePreview />,
                    },
                    {
                      path: ':dataset_id/upload',
                      element: <KnowledgeUpload />,
                    },
                  ],
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.KNOWLEDGE,
                    pageModeByQuery: true,
                  }),
                },

                // database resources
                {
                  path: 'database',
                  children: [
                    {
                      path: ':table_id',
                      element: <DatabaseDetail />,
                    },
                  ],
                  loader: () => ({
                    showMobileTips: true,
                    pageModeByQuery: true,
                  }),
                },

                // plugin resources
                {
                  path: 'plugin/:plugin_id',
                  Component: PluginLayout,
                  children: [
                    {
                      index: true,
                      Component: PluginPage,
                    },
                    {
                      path: 'tool/:tool_id',
                      children: [
                        {
                          index: true,
                          Component: PluginToolPage,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },

        // workflow routing
        {
          path: 'work_flow',
          Component: WorkflowPage,
          loader: () => ({
            hasSider: false,
            requireAuth: true,
          }),
        },

        // search
        {
          path: 'search/:word',
          Component: SearchPage,
          loader: () => ({
            hasSider: true,
            requireAuth: true,
          }),
        },

        // explore
        {
          path: 'explore',
          Component: null,
          loader: () => ({
            hasSider: true,
            requireAuth: true,
            subMenu: spaceSubMenu,
            menuKey: BaseEnum.Explore,
          }),
          children: [
            {
              index: true,
              element: <Navigate to="plugin" replace />,
            },
            // plugin store
            {
              path: 'plugin',
              element: <ExplorePluginPage />,
              loader: () => ({
                type: 'plugin',
              }),
            },
            // template
            {
              path: 'template',
              element: <ExploreTemplatePage />,
              loader: () => ({
                type: 'template',
              }),
            },
          ],
        },
      ],
    },
  ]);
